import { defaultPeriod } from '@/views/invoicing/show/commissionNote/components/commissionNoteCandidates'

export type CommissionNoteItem = {
  id: string | number
  is_free?: boolean
  amount_edited?: boolean
  rectification?: unknown
  /** A line whose invoice or order was cancelled: it stays in the open note, out of its totals */
  is_cancelled?: boolean
  name?: string | null
  description?: string | null
  internal_note?: string | null
  quantity?: number | string | null
  unit_price_htva?: number | string | null
  vat_rate?: number | string | null
  commission_htva?: number | string | null
  document_date?: string | null
}

export type CommissionNoteItemForm = {
  name: string | null
  description: string | null
  quantity: number | string | null
  unit_price_htva: number | string | null
  vat_rate: number | string | null
  internal_note: string | null
  commission_htva: number | string | null
  /** YYYY-MM-DD, free lines only */
  date: string | null
}

const toNumber = (value: number | string | null | undefined) =>
  value === null || value === undefined || value === ''
    ? null
    : Number(String(value).replace(',', '.'))

const toText = (value: string | null | undefined) =>
  value && value.trim().length ? value : null

/** The form of a line, from the line itself (editing) or empty, dated today (adding a free line). */
export function itemToForm(
  item: CommissionNoteItem | null,
  today: string = defaultPeriod().to
): CommissionNoteItemForm {
  return {
    name: item?.name ?? null,
    description: item?.description ?? null,
    quantity: item?.quantity ?? 1,
    unit_price_htva: item?.unit_price_htva ?? null,
    // empty for a new line: the API applies the default rate of the country of the agency
    vat_rate: item?.vat_rate ?? null,
    internal_note: item?.internal_note ?? null,
    commission_htva: item?.commission_htva ?? null,
    date: item?.document_date?.slice(0, 10) ?? today
  }
}

/**
 * What the API expects. A calculated line only takes a description, an internal
 * note, a VAT rate and an amount, and the amount is sent only when it really
 * changed: sending it stops the automatic recalculation of the line.
 */
export function itemPayload(
  item: CommissionNoteItem | null,
  form: CommissionNoteItemForm
): Record<string, unknown> {
  if (item && !item.is_free) {
    const payload: Record<string, unknown> = {
      description: toText(form.description),
      internal_note: toText(form.internal_note),
      vat_rate: toNumber(form.vat_rate)
    }

    if (toNumber(form.commission_htva) !== toNumber(item.commission_htva)) {
      payload.commission_htva = toNumber(form.commission_htva)
    }

    return payload
  }

  return {
    name: toText(form.name),
    description: toText(form.description),
    quantity: toNumber(form.quantity) ?? 1,
    unit_price_htva: toNumber(form.unit_price_htva),
    vat_rate: toNumber(form.vat_rate) ?? undefined,
    internal_note: toText(form.internal_note),
    date: form.date || undefined
  }
}

/** Visual amount of a free line while it is typed: the API sends the exact total. */
export function freeLineAmount(form: CommissionNoteItemForm): number | null {
  const quantity = toNumber(form.quantity) ?? 1
  const unitPrice = toNumber(form.unit_price_htva)

  return unitPrice === null
    ? null
    : Math.round(quantity * unitPrice * 100) / 100
}

/** A correction line (credit note) cannot be edited, nor deleted by hand. */
export const isEditableItem = (item: CommissionNoteItem) => !item.rectification

/**
 * A line can be deleted when it can be edited, and also when it is cancelled: its
 * document was cancelled, nothing else depends on it, and the note keeps no trace of
 * a cancelled line it does not need. A real correction line (it corrects another
 * line) stays: the API refuses to delete it.
 */
export const isDeletableItem = (item: CommissionNoteItem) =>
  isEditableItem(item) || item.is_cancelled === true

/** The ids of every line in the order to send to the API. */
export const orderPayload = (items: CommissionNoteItem[]) =>
  items.map((item) => item.id)

/** Amount with the VAT of its line, rounded to the cent. */
export function withVat(
  htva: number | string | null | undefined,
  vatRate: number | string | null | undefined
): number {
  return (
    Math.round(
      (parseFloat(String(htva)) || 0) *
        (1 + (parseFloat(String(vatRate)) || 0) / 100) *
        100
    ) / 100
  )
}

/**
 * Rounds to the cent, half away from zero: as the ROUND of the database, which
 * the API totals of a note are made with. (`Math.round` rounds a negative half
 * the other way.)
 */
export const roundCent = (value: number): number =>
  (Math.sign(value) * Math.round(Math.abs(value) * 100 + 1e-9)) / 100 || 0

/** The VAT of one line, rounded to the cent as the API does before adding the lines up. */
export function vatOf(
  htva: number | string | null | undefined,
  vatRate: number | string | null | undefined
): number {
  return roundCent(
    ((parseFloat(String(htva)) || 0) * (parseFloat(String(vatRate)) || 0)) / 100
  )
}

/** Negative amounts (credit notes, corrections) are red. */
export const amountClass = (value: number | string | null | undefined) =>
  parseFloat(String(value)) < 0 ? 'text-red-600' : ''
